import {ChangeEvent, useCallback, useEffect, useRef, useState} from "react";
import {ChatService} from "../../api/chat-bot";
import {Box, Button, Icon, Skeleton, Stack, TextField, Typography} from "@mui/material";
import "./chat.css"
import {Questions} from "../../constants/ChatQuestions";
import {ErrorDetailContainer} from "../../api/config";
import {ErrorParser, Spinner} from "../../components/exports";
import Markdown from "react-markdown";
import {useSocketContext} from "../../contexts/SocketContext";

export type Conversation = {
    id: string
    content: string
    isFromUser: boolean
}

export default function Chat() {

    const {socket} = useSocketContext();

    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [prompt, setPrompt] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<ErrorDetailContainer | null>(null);
    const [conversationId, setConversationId] = useState<string | null>(null);

    const messagesEndRef = useRef(null);

    const setMessage = (messageId: string, partialResponse: string) => {
        setConversations(prevState => {
            const message = prevState.find(c => c.id === messageId);
            if(!message) return prevState;
            return [...prevState.filter(p => p.id !== message.id), {
                ...message,
                content: message.content + partialResponse
            }]
        });

        scrollToBottom();
    }

    useEffect(() => {
        if (socket !== null) {
            socket.on('PartialResponse', data => {
                const response = JSON.parse(data);
                const messageId = response.messageId;
                const partialResponse = response.partialResponse;

                setMessage(messageId, partialResponse)
            });
        }
    }, [socket]);

    const uploadPrompt = async (prompt: string) => {

        if (isLoading || prompt.length === 0) {
            return
        }

        setConversations(prevState => {
            return [...prevState, {id: `${prevState.length + 1}`, content: prompt, isFromUser: true}]
        })

        scrollToBottom();

        setPrompt("");

        setIsLoading(true);

        try {
            const result = await ChatService.postApiChat({
                prompt: prompt,
                conversationId: conversationId,
                isAsync: true
            });

            setConversationId(result.conversationId);

            setConversations(prevState => {
                return [...prevState, {id: result.asyncMessageId, content: "", isFromUser: false}]
            });


        } catch (e: any) {
            const error = e as ErrorDetailContainer;
            setError(error);
            scrollToBottom();
        } finally {
            setIsLoading(false);
        }

    }

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({behavior: 'instant', block: "end",})
        }, 0)
    }

    const handleSendUserPrompt = () => {
        uploadPrompt(prompt).then()
    }

    const handlePromptChange = (e: ChangeEvent<HTMLInputElement>) => {
        setPrompt(e.target.value);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            setTimeout(() => {
                uploadPrompt(prompt).then();
            })
        }
    }

    const Question = (props: { children: string }) => {
        return <Box onClick={() => {
            setPrompt(props.children);
            uploadPrompt(props.children).then();
        }} sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            ':hover': {backgroundColor: 'background.default'},
            p: '8px 12px',
            cursor: 'pointer'
        }}>{props.children}</Box>
    };


    return <Box
        sx={{height: 'calc(65dvh - 32px)', width: '350px', display: 'flex', flexDirection: 'column', py: 1}}>

        {
            socket !== null ? (
                <>
                    <Stack gap={2} sx={{flex: 1, overflowY: 'auto', scrollbarWidth: 0, px: 2}}>

                        {
                            conversations.length === 0 &&
                            <Stack gap={2}>
                                <Typography>سلام👋👋</Typography>
                                <Typography color='text.secondary'
                                            className='place-holder'>
                                    سوال‌های خود را در رابطه با فروش و بازاریابی در صنعت ساختمان و ارتباط با سازندگان اینجا بپرسید
                                </Typography>

                                {
                                    Questions.map(q => {
                                        return <Question key={q.id}>
                                            {q.question}
                                        </Question>
                                    })
                                }

                            </Stack>
                        }


                        {
                            conversations.map(c => {
                                return (
                                    <span
                                        key={c.id}
                                        className={`bubble ${c.isFromUser ? 'user' : 'bot'}`}>
                                            <Markdown>{c.content}</Markdown>
                                    </span>
                                )
                            })
                        }

                        {
                            isLoading &&
                            <Skeleton className='bot' sx={{borderRadius: '12px'}}
                                      variant="rounded"
                                      width={120}
                                      height={40}>
                            </Skeleton>
                        }

                        <ErrorParser error={error}/>

                        <div ref={messagesEndRef}></div>

                    </Stack>

                    <Box sx={{position: 'relative', mx: 2, background: 'transparent'}}>

                        <TextField multiline
                                   rows={3}
                                   fullWidth
                                   placeholder="سوالتان را بپرسید"
                                   variant='outlined'
                                   onKeyDown={(e) => handleKeyDown(e)}
                                   value={prompt}
                                   onChange={handlePromptChange}/>

                        <Button
                            disabled={isLoading}
                            sx={{position: 'absolute', right: '8px', bottom: '8px'}}
                            variant='contained'
                            endIcon={<Icon fontSize="large">arrow_back</Icon>}
                            color='primary'
                            onClick={handleSendUserPrompt}>
                            ارسال
                        </Button>

                    </Box>
                </>
            ) : (
                <Spinner/>
            )
        }
    </Box>
}
